<template>
  <div class="content">
    <div class="flex-container">
      <h1 class="face-title mb-sm">{{ title }}</h1>
      <h2
        v-if="subtitle"
        class="face-subtitle heading-5"
      >
        {{ subtitle }}
      </h2>
    </div>

    <div
      v-if="intro || linkObject"
      class="face-footer pt-lg"
    >
      <div
        v-if="intro"
        class="face-intro"
        v-html="intro"
      />

      <UIContentblockButton
        v-if="linkObject?.name && linkObject?.link"
        class="btn btn-primary"
        :name="linkObject.name"
        :link="linkObject.link"
        :target="linkObject.target ?? '_self'"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  defineProps<{
    title: string
    subtitle?: string
    intro?: string
    linkObject?: {
      name?: string
      link?: string
      target?: string
    } | null
  }>()
</script>

<style scoped lang="scss">
  .face-footer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--margin-md);
  }

  .face-subtitle {
    margin: var(--margin-xs) 0 0;
    color: #17120e;
    @include line-clamp(2);
  }

  .face-intro {
    color: #17120e;
    @include line-clamp(4);
  }

  .content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100%;

    .face-title {
      overflow-wrap: break-word;
      font-size: var(--text-heading-2);
    }

    @include media(sm) {
      min-height: 55%;

      .face-title {
        font-size: var(--text-heading-3);
        overflow-wrap: break-word;
        hyphens: auto;
        hyphenate-limit-chars: 12 4 4;
      }

      .face-subtitle {
        font-size: var(--text-body);
      }

      .face-intro {
        font-size: 1.6rem;
        @include line-clamp(2);
      }

      .face-footer {
        .btn-primary {
          font-size: 1.6rem;
          padding: 0.8rem 2.2rem !important;
        }
      }
    }

    // Interpolates from $min at 630dvh to $max at 780dvh
    @function height-clamp($min, $max, $intercept, $coefficient) {
      @return clamp(#{$min}, calc(#{$intercept} + #{$coefficient}dvh), #{$max});
    }

    @media (max-height: 780px) and (min-width: #{$screen-sm + 1px}) {
      .face-title {
        font-size: height-clamp(var(--text-heading-3), var(--text-heading-2), -4.8rem, 13.333);
        margin-bottom: height-clamp(var(--margin-xs), var(--margin-sm), -1.72rem, 4);
      }

      .face-subtitle {
        font-size: height-clamp(var(--text-body), var(--text-heading-5), 0.64rem, 2);
      }

      .face-footer {
        gap: height-clamp(var(--margin-xs), var(--margin-md), -4.24rem, 8);
        padding-top: height-clamp(var(--padding-sm), var(--padding-lg), -4.48rem, 9.333) !important;

        .face-intro {
          font-size: height-clamp(1.6rem, 1.8rem, 0.76rem, 1.333);
        }

        .btn-primary {
          font-size: height-clamp(1.6rem, 1.8rem, 0.76rem, 1.333);
          padding: height-clamp(0.8rem, 1.4rem, -1.72rem, 4) height-clamp(2.2rem, 2.4rem, 1.36rem, 1.333) !important;
        }
      }

      @media (max-height: 780px) {
        .face-intro {
          @include line-clamp(2);
        }
      }
    }
  }
</style>
